import type { FetchQueryOptions, QueryClient, QueryKey } from '@tanstack/react-query';

// A route loader's read of the entity its page renders: the cached value when
// there is one, else one fetch into the same cache entry the page's own query
// reads, so the page never fetches it twice.
//
// `prefetchQuery` stores a failure in the cache instead of throwing, which is
// what a detail page wants: a 404 has to reach the page's branded
// NotFoundPage and anything else its retry surface, not the error boundary a
// thrown loader error lands in. The page's query must set
// `retryOnMount: false`, or it refetches the failure the loader left behind.
//
// A stale cached value is returned as it is; the page's query revalidates it.
export async function loadForRoute<TData, TError, TKey extends QueryKey>(
  queryClient: QueryClient,
  options: FetchQueryOptions<TData, TError, TData, TKey>,
): Promise<TData | undefined> {
  const cached = queryClient.getQueryData<TData>(options.queryKey);
  if (cached !== undefined) return cached;
  await queryClient.prefetchQuery(options);
  return queryClient.getQueryData<TData>(options.queryKey);
}
